/// ///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2025 CONQUER INNOVATIONS SP. Z O.O. Global B.V. and its affiliate(s).
// All rights reserved.
//
// This software and other materials contain proprietary information
// controlled by CONQUER INNOVATIONS SP. Z O.O. and are protected by applicable copyright legislation.
// Any use and utilization of this software and other materials and
// disclosure to any third parties is conditional upon having a separate
// agreement with CONQUER INNOVATIONS SP. Z O.O. for the access, use, utilization or disclosure of this
// software. In the absence of such agreement, the use of the software is not
// allowed.
// The creator, author and originator of the code in the current file: Michał Pawłowski
//////////////////////////////////////////////////////////////////////////////////
<template>
  <b-modal
      v-model="isVisible"
      id="table-column-manager-modal"
      title="Zarządzaj kolumnami tabeli"
      title-class="font-18 fw-bold text-primary"

      header-class="bg-light-gray border-bottom-0"

      footer-class="bg-light border-top"
      size="md"
      centered
      @hidden="resetSelection"
  >

                <button
                    v-if="exportEnabled"
                    class="d-none d-sm-inline-block btn btn-sm btn-info shadow-sm"
                    @click="exportTableWithWarning()"

                >
                  <i class="fas fa-file-excel ms-1"/>
                  Eksportuj
                </button>


    <div class="p-1">
      <div class="d-flex justify-content-between align-items-center mb-3 px-2">
        <span class="text-muted small">Wybierz kolumny do wyświetlenia:</span>
        <div class="btn-group btn-group-sm">
          <button class="btn btn-outline-secondary" @click="selectAll">Pokaż wszystkie</button>
          <button class="btn btn-outline-secondary" @click="deselectAll">Ukryj wszystkie</button>
        </div>
      </div>

      <div class="list-group shadow-sm" style="max-height: 400px; overflow-y: auto;">
        <label
            v-for="header in allHeaders"
            :key="header.key"
            class="list-group-item list-group-item-action d-flex align-items-center gap-3 cursor-pointer py-2"
            :class="{ 'bg-light': !selectedKeys.includes(header.key) }"
        >
          <input
              class="form-check-input flex-shrink-0 my-0"
              type="checkbox"
              :value="header.key"
              v-model="selectedKeys"
              style="cursor: pointer; width: 1.2em; height: 1.2em;"
          >
          <div class="d-flex flex-column">
            <span class="fw-medium text-dark">{{ header.text }}</span>
            <span class="small text-muted" style="font-size: 0.75rem;">Klucz: {{ header.key }}</span>
          </div>
        </label>
      </div>

      <div class="mt-2 px-2 text-end">
        <small class="text-muted">Wybrano: <strong>{{ selectedKeys.length }}</strong> z {{ allHeaders.length }}</small>
      </div>
    </div>

    <template #footer>
      <div class="w-100 d-flex justify-content-end gap-2">
        <button class="btn btn-secondary" @click="isVisible = false">Anuluj</button>
        <button class="btn btn-primary px-4" @click="saveChanges">
          <i class="fas fa-save me-1"></i> Zapisz widok
        </button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { BModal } from "bootstrap-vue-next";
import Swal from "sweetalert2";

export default {
  name: "TableColumnManagerModal",
  components: { BModal },
  props: {
    showModal: { type: Boolean, required: true },
    // Pełna lista wszystkich możliwych headerów (tablica obiektów)
    allHeaders: { type: Array, required: true, default: () => [] },
    // Opcjonalnie: lista kluczy aktualnie widocznych (żeby modal wiedział co zaznaczyć na start)
    // Jeśli nie podana, domyślnie zaznaczy wszystkie z allHeaders
    currentVisibleKeys: { type: Array, default: null },
    exportEnabled: { type: Boolean, default: false }
  },
  emits: ["update:showModal", "onApply","onExportTable"],
  data() {
    return {
      selectedKeys: []
    };
  },
  computed: {
    isVisible: {
      get() { return this.showModal; },
      set(val) { this.$emit("update:showModal", val); }
    }
  },
  watch: {
    // Kiedy modal się otwiera, inicjalizujemy stan checkboxów
    showModal(val) {
      if (val) {
        this.initializeSelection();
      }
    }
  },
  methods: {
    exportTableWithWarning(){
      Swal.fire({
        title: 'Czy na pewno chcesz wyeksportować dane?',
        text: "Ta operacja może potrwać kilka sekund w zależności od ilości danych.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Tak, eksportuj!',
        cancelButtonText: 'Nie, anuluj'
      }).then((result) => {
        if (result.isConfirmed) {
          console.log("currentVisibleKeys::", this.selectedKeys)
          this.$emit("onExportTable", {visibleHeaders:this.selectedKeys});
        }
      });

    },
    initializeSelection() {
      if (this.currentVisibleKeys && this.currentVisibleKeys.length > 0) {
        // Kopiujemy, żeby nie operować na referencji
        this.selectedKeys = [...this.currentVisibleKeys];
      } else {
        // Jeśli nie podano aktualnych, zaznaczamy wszystkie
        this.selectAll();
      }
    },

    selectAll() {
      this.selectedKeys = this.allHeaders.map(h => h.key);
    },

    deselectAll() {
      this.selectedKeys = [];
    },

    resetSelection() {
      // Opcjonalne czyszczenie przy zamknięciu, ale watcher handle'uje otwarcie
    },

    saveChanges() {
      // Filtrujemy oryginalną listę headerów, zachowując ich kolejność z definicji (allHeaders),
      // ale zwracamy tylko te, których klucz jest w selectedKeys.
      const filteredHeaders = this.allHeaders.filter(h => this.selectedKeys.includes(h.key));
      const filteredKeys = filteredHeaders.map(h => h.key);
      this.$emit("onApply", filteredKeys);
      this.isVisible = false;
    }
  },
  created() {
    // console.log("TableColumnManagerModal created.");
    // console.log(this.allHeaders);
  }
};
</script>

<style scoped>
.bg-light-gray {
  background-color: #f8f9fa;
}

.cursor-pointer {
  cursor: pointer;
}

/* Custom checkbox styling override if needed, otherwise Bootstrap defaults apply */
.form-check-input:checked {
  background-color: #0078D4;
  border-color: #0078D4;
}

.list-group-item:hover {
  background-color: #f1f1f1;
}

.list-group-item.bg-light {
  background-color: #fafafa !important;
  opacity: 0.8;
}
</style>